import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { FileText, Calculator, ShieldCheck, ArrowRight, Cpu, Users } from 'lucide-react';

const DesktopTaxReturnLanding = () => {
  const processSteps = [
    {
      icon: FileText,
      title: "1. Provide Details",
      description: "Fill out our secure, intuitive form with your income and tax information.",
    },
    {
      icon: Cpu,
      title: "2. We Process & Verify",
      description: "Our system, backed by experts, accurately processes your return and ensures compliance.",
    },
    {
      icon: ShieldCheck,
      title: "3. Receive Confirmation",
      description: "Get your finalized documents and FBR confirmation, all handled by us.",
    },
  ];

  return (
    <>
      <style>{`
        @keyframes animated-gradient-text {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .animated-gradient-text {
          background: linear-gradient(to right, #3b82f6, #6366f1, #8b5cf6, #3b82f6);
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: animated-gradient-text 5s linear infinite;
        }
        .service-card:hover .arrow-icon {
          transform: translateX(5px);
        }
        .glass-card {
           background: rgba(255, 255, 255, 0.7);
           backdrop-filter: blur(10px);
           border: 1px solid rgba(255, 255, 255, 0.5);
        }
      `}</style>

      <div className="min-h-screen flex flex-col bg-slate-50/80">
        <Header />

        {/* Hero Section - Standardized */}
        <section className="relative pt-24 pb-20 lg:pt-36 lg:pb-32 bg-slate-50/80 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:20px_20px] [mask-image:radial-gradient(ellipse_50%_50%_at_50%_50%,#000_70%,transparent_100%)]"></div>

          <div className="relative z-10 max-w-5xl mx-auto px-4 text-center">
            <h1 className="text-3xl sm:text-5xl md:text-7xl font-extrabold tracking-tight text-slate-900 mb-6 sm:mb-8">
              Intelligent Tax Filing, <br />
              <span className="animated-gradient-text">Engineered for Clarity.</span>
            </h1>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto mb-10 leading-relaxed">
              Leverage our advanced platform for seamless FBR compliance, tax registration, and annual return filing in Pakistan.
            </p>
            <div className="flex justify-center gap-4">
              <Button size="lg" className="text-lg h-14 px-8 shadow-lg hover:shadow-xl transition-all duration-300 bg-blue-600 hover:bg-blue-700" asChild>
                <Link to="/tax-return">
                  <FileText className="h-5 w-5 mr-2.5" />
                  Start Filing
                </Link>
              </Button>
            </div>
          </div>
        </section>

        <main className="flex-grow flex flex-col items-center justify-center px-4 -mt-10 relative z-10">

          <section className="w-full max-w-6xl pb-16 md:pb-24">
            <div className="text-center mb-16">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900">A Smarter Way to File</h2>
              <p className="mt-3 text-lg text-slate-600">Our streamlined 3-step process simplifies everything.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {processSteps.map((step) => (
                <div
                  key={step.title}
                  className="glass-card p-8 rounded-2xl text-center shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 mb-6 bg-blue-50 rounded-2xl shadow-sm">
                    <step.icon className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{step.title}</h3>
                  <p className="text-slate-700">{step.description}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="w-full max-w-6xl pb-16 md:pb-24">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <Link to="/tax-return" className="block group service-card lg:col-span-2">
                <div className="w-full h-full bg-gradient-to-br from-blue-600 to-indigo-700 p-8 md:p-12 rounded-2xl text-white shadow-xl hover:shadow-2xl transition-all duration-300 ease-in-out hover:-translate-y-1">
                  <FileText className="h-12 w-12 mb-4 opacity-80" />
                  <h3 className="text-xl sm:text-2xl md:text-4xl font-bold mb-3">File Your Tax Return</h3>
                  <p className="text-lg opacity-90 mb-8">Launch our guided workflow to file your annual tax return with precision and ease.</p>
                  <div className="flex items-center font-semibold text-lg bg-white/20 hover:bg-white/30 transition-colors rounded-full py-2 px-5 w-fit">
                    Proceed to Filing <ArrowRight className="h-5 w-5 ml-3 transition-transform duration-300 arrow-icon" />
                  </div>
                </div>
              </Link>
              <div className="flex flex-col gap-8">
                <Link to="/tax-calculator" className="block group service-card flex-1">
                  <div className="w-full h-full bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 ease-in-out hover:-translate-y-1 border border-slate-100">
                    <Calculator className="h-10 w-10 mb-4 text-blue-600" />
                    <h3 className="text-2xl font-bold mb-2 text-slate-900">Tax Calculator</h3>
                    <p className="text-slate-600 mb-4">Estimate your tax liability with our free, powerful tool.</p>
                    <div className="flex items-center font-semibold text-blue-600">
                      Calculate Now <ArrowRight className="h-5 w-5 ml-2 transition-transform duration-300 arrow-icon" />
                    </div>
                  </div>
                </Link>
                <Link to="/contact" className="block group service-card flex-1">
                  <div className="w-full h-full bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 ease-in-out hover:-translate-y-1 border border-slate-100">
                    <Users className="h-10 w-10 mb-4 text-blue-600" />
                    <h3 className="text-2xl font-bold mb-2 text-slate-900">Expert Help</h3>
                    <p className="text-slate-600 mb-4">Connect with a certified tax professional for personalized advice.</p>
                    <div className="flex items-center font-semibold text-blue-600">
                      Contact Us <ArrowRight className="h-5 w-5 ml-2 transition-transform duration-300 arrow-icon" />
                    </div>
                  </div>
                </Link>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default DesktopTaxReturnLanding;
